import {
    type Cvss2ScoringEngine,
    type Cvss3ScoringEngine,
    Cvss2VectorParser,
    Cvss3VectorParser,
    cvss2,
    cvss3,
} from '@ics-portal/cvss';
import { type Maybe } from '@models/Maybe';
import { CvssVersionLabel } from '@components/cve/cve-details/types';
import { SeverityRating } from '@utils/cvss';

export interface CvssRadarChartData {
    labels: string[];
    values: number[];
    metricLabels: string[];
    severityRatings: Maybe<SeverityRating>[];
}

function getSeverityRatingFromMetricLabel(
    metricLabel: string,
): Maybe<SeverityRating> {
    const normalizedLabel = metricLabel.toLowerCase();
    const matchedRating = Object.values(SeverityRating).find(
        (rating) => rating.toLowerCase() === normalizedLabel,
    );
    return matchedRating ?? null;
}

type AttackVectorValue = Cvss3ScoringEngine['attackVector'];
type AttackComplexityValue = Cvss3ScoringEngine['attackComplexity'];
type PrivilegesRequiredValue = Cvss3ScoringEngine['privilegesRequired'];
type UserInteractionValue = Cvss3ScoringEngine['userInteraction'];
type ScopeValue = Cvss3ScoringEngine['scope'];
type ImpactValue = Cvss3ScoringEngine['confidentialityImpact'];

type AccessVectorValue = Cvss2ScoringEngine['accessVector'];
type AccessComplexityValue = Cvss2ScoringEngine['accessComplexity'];
type AuthenticationValue = Cvss2ScoringEngine['authentication'];
type Cvss2ImpactValue = Cvss2ScoringEngine['confidentialityImpact'];

function humanizeEnumValue(value: string): string {
    return value
        .toLowerCase()
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

function getAttackVectorWeight(attackVector: AttackVectorValue): number {
    switch (attackVector) {
        case cvss3.AttackVector.Network:
            return 0.85;
        case cvss3.AttackVector.AdjacentNetwork:
            return 0.62;
        case cvss3.AttackVector.Local:
            return 0.55;
        case cvss3.AttackVector.Physical:
            return 0.2;
        default:
            return 0;
    }
}

function getAttackComplexityWeight(
    attackComplexity: AttackComplexityValue,
): number {
    switch (attackComplexity) {
        case cvss3.AttackComplexity.Low:
            return 0.77;
        case cvss3.AttackComplexity.High:
            return 0.44;
        default:
            return 0;
    }
}

function getPrivilegesRequiredWeight(
    privilegesRequired: PrivilegesRequiredValue,
    scope: ScopeValue,
): number {
    if (privilegesRequired === cvss3.PrivilegesRequired.None) return 0.85;

    const scopeChanged = scope === cvss3.Scope.Changed;

    if (privilegesRequired === cvss3.PrivilegesRequired.Low) {
        return scopeChanged ? 0.68 : 0.62;
    }
    if (privilegesRequired === cvss3.PrivilegesRequired.High) {
        return scopeChanged ? 0.5 : 0.27;
    }
    return 0;
}

function getUserInteractionWeight(
    userInteraction: UserInteractionValue,
): number {
    switch (userInteraction) {
        case cvss3.UserInteraction.None:
            return 0.85;
        case cvss3.UserInteraction.Required:
            return 0.62;
        default:
            return 0;
    }
}

function getImpactWeight(impact: ImpactValue): number {
    switch (impact) {
        case cvss3.Impact.None:
            return 0;
        case cvss3.Impact.Low:
            return 0.22;
        case cvss3.Impact.High:
            return 0.56;
        default:
            return 0;
    }
}

function getScopeChartValue(scope: ScopeValue): number {
    return scope === cvss3.Scope.Changed ? 100 : 20;
}

function getCvss3ScoringEngine(vector: string): Cvss3ScoringEngine {
    return new Cvss3VectorParser().generateScoringEngine(vector);
}

function buildCvss3RadarChartData(vector: string): CvssRadarChartData {
    const engine = getCvss3ScoringEngine(vector);

    const labels = [
        'Attack Vector',
        'Attack Complexity',
        'Privileges Required',
        'User Interaction',
        'Scope',
        'Confidentiality Impact',
        'Integrity Impact',
        'Availability Impact',
    ];

    const values = [
        getAttackVectorWeight(engine.attackVector) * 100,
        getAttackComplexityWeight(engine.attackComplexity) * 100,
        getPrivilegesRequiredWeight(engine.privilegesRequired, engine.scope) *
            100,
        getUserInteractionWeight(engine.userInteraction) * 100,
        getScopeChartValue(engine.scope),
        getImpactWeight(engine.confidentialityImpact) * 100,
        getImpactWeight(engine.integrityImpact) * 100,
        getImpactWeight(engine.availabilityImpact) * 100,
    ];

    const metricLabels = [
        humanizeEnumValue(engine.attackVector),
        humanizeEnumValue(engine.attackComplexity),
        humanizeEnumValue(engine.privilegesRequired),
        humanizeEnumValue(engine.userInteraction),
        humanizeEnumValue(engine.scope),
        humanizeEnumValue(engine.confidentialityImpact),
        humanizeEnumValue(engine.integrityImpact),
        humanizeEnumValue(engine.availabilityImpact),
    ];

    const severityRatings = metricLabels.map(getSeverityRatingFromMetricLabel);

    return { labels, values, metricLabels, severityRatings };
}

function getAccessVectorWeight(accessVector: AccessVectorValue): number {
    switch (accessVector) {
        case cvss2.AccessVector.Network:
            return 1.0;
        case cvss2.AccessVector.AdjacentNetwork:
            return 0.646;
        case cvss2.AccessVector.Local:
            return 0.395;
        default:
            return 0;
    }
}

function getAccessComplexityWeight(
    accessComplexity: AccessComplexityValue,
): number {
    switch (accessComplexity) {
        case cvss2.AccessComplexity.Low:
            return 0.71;
        case cvss2.AccessComplexity.Medium:
            return 0.61;
        case cvss2.AccessComplexity.High:
            return 0.35;
        default:
            return 0;
    }
}

function getAuthenticationWeight(authentication: AuthenticationValue): number {
    switch (authentication) {
        case cvss2.Authentication.None:
            return 0.704;
        case cvss2.Authentication.Single:
            return 0.56;
        case cvss2.Authentication.Multiple:
            return 0.45;
        default:
            return 0;
    }
}

function getCvss2ImpactWeight(impact: Cvss2ImpactValue): number {
    switch (impact) {
        case cvss2.Impact.None:
            return 0;
        case cvss2.Impact.Partial:
            return 0.275;
        case cvss2.Impact.Complete:
            return 0.66;
        default:
            return 0;
    }
}

function getCvss2ScoringEngine(vector: string): Cvss2ScoringEngine {
    return new Cvss2VectorParser().generateScoringEngine(vector);
}

function buildCvss2RadarChartData(vector: string): CvssRadarChartData {
    const engine = getCvss2ScoringEngine(vector);

    const labels = [
        'Access Vector',
        'Access Complexity',
        'Authentication',
        'Confidentiality Impact',
        'Integrity Impact',
        'Availability Impact',
    ];

    const values = [
        getAccessVectorWeight(engine.accessVector) * 100,
        getAccessComplexityWeight(engine.accessComplexity) * 100,
        getAuthenticationWeight(engine.authentication) * 100,
        getCvss2ImpactWeight(engine.confidentialityImpact) * 100,
        getCvss2ImpactWeight(engine.integrityImpact) * 100,
        getCvss2ImpactWeight(engine.availabilityImpact) * 100,
    ];

    const metricLabels = [
        humanizeEnumValue(engine.accessVector),
        humanizeEnumValue(engine.accessComplexity),
        humanizeEnumValue(engine.authentication),
        humanizeEnumValue(engine.confidentialityImpact),
        humanizeEnumValue(engine.integrityImpact),
        humanizeEnumValue(engine.availabilityImpact),
    ];

    const severityRatings = metricLabels.map(getSeverityRatingFromMetricLabel);

    return { labels, values, metricLabels, severityRatings };
}

export function buildCvssRadarChartData(
    vector: string,
    version: CvssVersionLabel,
): CvssRadarChartData {
    if (version === CvssVersionLabel.V2) {
        return buildCvss2RadarChartData(vector);
    }
    return buildCvss3RadarChartData(vector);
}
